import { Box, CircularProgress, MenuItem } from '@material-ui/core';
import { I18n } from 'aws-amplify';
import { useFormikContext } from 'formik';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import DomainsClient from 'src/clients/DomainsClient';
import * as Constants from 'src/constants';
import { PortalUrlValidation } from 'src/constants/errorConsts/errorCodesConsts';
import {
  assemblyClientDomainHostname,
  copilotClientDomainHostname,
  reservedSubdomains,
} from 'src/constants/hostnameConsts';
import { useIsAssemblyBrandEnabled } from 'src/hooks/useIsAssemblyBrandEnabled';
import {
  ErrorCloseMarkIcon,
  SuccessCheckmarkIcon,
} from 'src/legacy/components/Icons';
import OnboardingButton from 'src/legacy/components/Onboarding/OnboardingButton';
import { OnboardingFormError } from 'src/legacy/components/Onboarding/OnboardingForms/OnboardingFormError';
import { getFormErrorText } from 'src/legacy/components/Onboarding/utils';
import RowDivider from 'src/legacy/components/RowDivider';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { SharpOutlinedTextField } from 'src/legacy/components/TextField';
import { LegacyOnboardingPageHeader } from 'src/routes/onboarding/setup/components/LegacyOnboardingPageHeader';
import { useSetFormPageInfo } from 'src/routes/onboarding/setup/context/formPageInfoContext';
import {
  OnboardingFormValues,
  OnboardingStep,
  TrackedEventsType,
} from 'src/routes/onboarding/setup/types';
import { TrackEvent } from 'src/utils/AnalyticsUtils';
import { ensureApiError } from 'src/utils/Errors';
import { filterNonAlphanumericCharacters } from 'src/utils/StringUtils';

const validationSchema = toFormikValidationSchema(
  z.intersection(
    z.object({
      name: z
        .string({ required_error: 'Name is required' })
        .regex(
          /^([a-zA-Z]{1,}\s[a-zA-Z]{1,}'?-?[a-zA-Z]{1,}\s?([a-zA-Z]{1,})?)/,
          'Please enter first and last name',
        ),
      portalUrl: z
        .string({
          required_error: Constants.PORTAL_URL_REQUIRED_ERROR,
        })
        .min(4, Constants.PORTAL_URL_MUST_BE_MORE_THAN_4)
        .max(30, Constants.PORTAL_URL_MUST_BE_LESS_THAN_30)
        .refine((val) => !reservedSubdomains.includes(val), {
          message: Constants.PORTAL_NAME_INCLUDES_RESERVED_WORD,
        }),
      companyName: z
        .string({ required_error: 'Company name is required' })
        .trim()
        .min(1, 'Company name is required')
        .max(30, 'Company name must be less than 30 characters'),
    }),
    z
      .object({
        foundBy: z.string({ required_error: 'How you found us is required' }),
        foundByCustom: z.string().optional(),
      })
      .superRefine(({ foundBy, foundByCustom }, ctx) => {
        if (foundBy === Constants.FOUND_PORTAL_OTHER_VALUE) {
          if (!foundByCustom) {
            ctx.addIssue({
              code: 'custom',
              message: 'How you found us is required',
              path: ['foundByCustom'],
            });
          } else {
            const trimmed = foundByCustom.trim();
            if (trimmed.length < 4) {
              ctx.addIssue({
                code: 'too_small',
                minimum: 4,
                inclusive: false,
                type: 'string',
                message: 'How you found us must have at least 4 characters',
                path: ['foundByCustom'],
              });
            }
            if (trimmed.length > 50) {
              ctx.addIssue({
                code: 'too_big',
                maximum: 50,
                inclusive: true,
                type: 'string',
                message: 'How you found us must not exceed 50 characters',
                path: ['foundByCustom'],
              });
            }
          }
        }
      }),
  ),
);

const getFoundByOptions = () => {
  const foundByOptions = [...Constants.FOUND_PORTAL_SOURCES];
  for (let i = foundByOptions.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [foundByOptions[i], foundByOptions[j]] = [
      foundByOptions[j],
      foundByOptions[i],
    ];
  }

  return [
    ...foundByOptions,
    {
      id: 100,
      label: 'Other',
      value: Constants.FOUND_PORTAL_OTHER_VALUE,
    },
  ];
};

export const CompanySetup = ({
  trackedEvents,
  onAnalyticsEventTracked,
}: {
  trackedEvents: TrackedEventsType;
  onAnalyticsEventTracked: (eventName: OnboardingStep) => void;
}) => {
  const { t } = useTranslation();
  const { isAssemblyBrandEnabled } = useIsAssemblyBrandEnabled();
  const [portalUrlError, setPortalUrlError] = React.useState('');
  const [isValidating, setIsValidating] = React.useState(false);
  const foundPortalByOptions = getFoundByOptions();

  const {
    dirty,
    errors,
    handleBlur,
    handleChange,
    isValid,
    setFieldTouched,
    setFieldValue,
    touched,
    values,
  } = useFormikContext<OnboardingFormValues>();
  const [otherIsSelected, setOtherIsSelected] = React.useState(
    values.foundBy === Constants.FOUND_PORTAL_OTHER_VALUE,
  );

  const setFormPageInfo = useSetFormPageInfo<OnboardingFormValues>();
  useEffect(() => {
    setFormPageInfo({
      submitHandler: ({ values: submitValues, formikHelpers }) => {
        const valuesObj = { ...submitValues };
        if (!portalUrlError) {
          const domain = isAssemblyBrandEnabled
            ? assemblyClientDomainHostname.prod
            : copilotClientDomainHostname.prod;

          valuesObj.signoutRedirectURL = `${submitValues.portalUrl}.${domain}`;
          if (!trackedEvents['company-setup']) {
            TrackEvent('Onboarding - Portal Workspace Created', {
              name: submitValues.name,
              source: submitValues.foundBy,
              source_other: submitValues.foundByCustom,
              phone: submitValues.phone,
              company_name: submitValues.companyName,
              portal_URL: submitValues.portalUrl,
            });

            // update the tracked events
            onAnalyticsEventTracked('company-setup');
          }
        }
        formikHelpers.setValues(valuesObj);
      },
      validationSchema,
    });
  }, [portalUrlError, TrackEvent, trackedEvents, onAnalyticsEventTracked]);

  const validatePortalUrl = async (portalUrl: string) => {
    if (portalUrl.length < 4) {
      setPortalUrlError(Constants.PORTAL_URL_MUST_BE_MORE_THAN_4);
      setIsValidating(false);
      return;
    }
    if (portalUrl.length > 30) {
      setPortalUrlError(Constants.PORTAL_URL_MUST_BE_LESS_THAN_30);
      setIsValidating(false);
      return;
    }
    if (reservedSubdomains.includes(portalUrl)) {
      setPortalUrlError(Constants.PORTAL_NAME_INCLUDES_RESERVED_WORD);
      setIsValidating(false);
      return;
    }
    setIsValidating(true);
    try {
      await DomainsClient.ValidatePortalUrl(portalUrl);
      setPortalUrlError('');
      setIsValidating(false);
    } catch (err) {
      const error = ensureApiError(err);
      if (error.code === PortalUrlValidation.invalid) {
        setPortalUrlError('A portal with that name already exists.');
      } else {
        setPortalUrlError(
          'This Portal URL could not be used. Contact support for further assistance.',
        );
      }
      setIsValidating(false);
    }
  };

  const debounceAsyncFieldChange = useCallback(
    debounce((portalUrl: string) => validatePortalUrl(portalUrl), 1300),
    [],
  );

  const handleAsyncFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    event.persist();
    if (event.target.name === 'companyName') {
      handleChange(event);
    }

    const formattedPortalUrl = filterNonAlphanumericCharacters(
      event.target.value,
    ).toLowerCase();

    setFieldValue('portalUrl', formattedPortalUrl);

    setIsValidating(false);
    setPortalUrlError('');
    if (formattedPortalUrl.length < 4) {
      setPortalUrlError(Constants.PORTAL_URL_MUST_BE_MORE_THAN_4);
    } else if (formattedPortalUrl.length > 30) {
      setPortalUrlError(Constants.PORTAL_URL_MUST_BE_LESS_THAN_30);
    } else if (reservedSubdomains.includes(formattedPortalUrl)) {
      setPortalUrlError(Constants.PORTAL_NAME_INCLUDES_RESERVED_WORD);
    } else {
      setIsValidating(true);
    }

    debounceAsyncFieldChange(formattedPortalUrl);
  };

  const showIcon =
    !isValidating &&
    values.portalUrl.length >= 4 &&
    values.portalUrl.length <= 30;

  // portalURL error should be shown if there is an error in the portalUrl field
  // or from the async event. It should also be shown if we touched the portalUrl field
  // or the companyName fields because companyName automatically sets the value for portalUrl
  const portalUrlFieldError =
    ((touched.portalUrl || touched.companyName) && !!errors.portalUrl) ||
    !!portalUrlError;
  // disable the submit button
  // if user have not touched the form
  // or if there are errors and form is not valid
  // or if user have chosen other company option and not filled the custom value
  const isSubmitDisabled =
    !dirty || !isValid || (otherIsSelected && !values.foundByCustom);

  return (
    <>
      <LegacyOnboardingPageHeader
        title={t(($) => $.page_onboarding_company.create_account.title)}
        subtitle={t(($) => $.page_onboarding_company.create_account.subtitle)}
      />
      <div className="-mt-5">
        <SharpOutlinedTextField
          sizeVariant="tall"
          InputProps={{
            'data-testid': 'nameInput',
          }}
          id="name"
          autoFocus
          fullWidth
          type="text"
          name="name"
          variant="outlined"
          onBlur={handleBlur}
          onInput={handleChange}
          value={values.name}
          label={I18n.get('Full name')}
          placeholder={I18n.get('Full name')}
          error={Boolean(touched.name && errors.name)}
          helperText={' '}
        />
        <SharpOutlinedTextField
          select
          variant="outlined"
          InputProps={{
            'data-testid': 'foundbyInput',
          }}
          sizeVariant="tall"
          name="foundBy"
          error={Boolean(touched.foundBy && errors.foundBy)}
          helperText={' '}
          InputLabelProps={{ shrink: false }}
          label="How did you find us?"
          value={values.foundBy}
          onChange={(e) => {
            setOtherIsSelected(
              e.target.value === Constants.FOUND_PORTAL_OTHER_VALUE,
            );
            handleChange(e);
          }}
          onBlur={handleBlur}
          fullWidth
        >
          {foundPortalByOptions.map((portalSourceOption) => (
            <MenuItem
              value={portalSourceOption.value}
              key={portalSourceOption.id}
              data-testid={`option-${portalSourceOption.value}`}
            >
              {portalSourceOption.label}
            </MenuItem>
          ))}
        </SharpOutlinedTextField>
        {otherIsSelected && (
          <SharpOutlinedTextField
            id="foundByCustom"
            sizeVariant="tall"
            InputProps={{
              'data-testid': 'foundByCustomInput',
            }}
            fullWidth
            type="text"
            name="foundByCustom"
            value={values.foundByCustom}
            variant="outlined"
            onBlur={handleBlur}
            onChange={handleChange}
            label="Please specify"
            placeholder=""
            error={Boolean(touched.foundByCustom && errors.foundByCustom)}
            helperText={' '}
            autoFocus
          />
        )}
        <RowDivider mt={3} mb={0.5} />
        <SharpOutlinedTextField
          id="companyName"
          fullWidth
          sizeVariant="tall"
          type="text"
          name="companyName"
          variant="outlined"
          value={values.companyName}
          onBlur={(e) => {
            // set portal url touched once company name is touched
            // so that we can show portal url error
            setFieldTouched('portalUrl');
            handleBlur(e);
          }}
          onInput={handleAsyncFieldChange}
          label={t(($) => $.company_name)}
          placeholder={t(($) => $.company_name)}
          error={Boolean(touched.companyName && errors.companyName)}
          helperText={' '}
          InputProps={{
            'data-testid': 'companyNameInput',
          }}
        />
        <Box position="relative">
          <SharpOutlinedTextField
            sizeVariant="tall"
            variant="outlined"
            id="portalUrl"
            fullWidth
            type="text"
            name="portalUrl"
            value={values.portalUrl}
            onBlur={handleBlur}
            onInput={handleAsyncFieldChange}
            error={Boolean(portalUrlFieldError)}
            helperText={''}
            label={I18n.get('Portal URL')}
            InputProps={{
              classes: {
                root:
                  'pr-0 ' +
                  (isAssemblyBrandEnabled
                    ? 'w-[calc(100%-130px)]'
                    : 'w-[calc(100%-100px)]'),
                input: 'text-right text-sm font-normal !pr-2',
              },
              'data-testid': 'portalUrlInput',
              endAdornment: (
                <div className="flex items-center text-black-heading absolute left-full">
                  <BaseTypography color="inherit">
                    {isAssemblyBrandEnabled
                      ? '.myassembly.com'
                      : '.copilot.app'}
                  </BaseTypography>
                  <div className="ml-2 flex items-center w-5">
                    {isValidating && (
                      <CircularProgress
                        classes={{ root: 'text-black-heading' }}
                        size={20}
                      />
                    )}
                    {showIcon &&
                      (!portalUrlError ? (
                        <SuccessCheckmarkIcon
                          fontSize="small"
                          data-testid="subdomain-verified"
                          className="text-medium-dark-green"
                        />
                      ) : (
                        <ErrorCloseMarkIcon fontSize="small" />
                      ))}
                  </div>
                </div>
              ),
            }}
          />
          <OnboardingFormError
            errorText={getFormErrorText(errors, touched) || portalUrlError}
          />
        </Box>

        <OnboardingButton
          type="submit"
          data-testid="submit-button"
          color="primary"
          variant="contained"
          fullWidth
          htmlId="create-portal-submit-button"
          disabled={isSubmitDisabled || isValidating || Boolean(portalUrlError)}
        >
          Continue
        </OnboardingButton>
      </div>
    </>
  );
};
